<template>
  <div>
    <menu-top/>

    <v-main>
      <div id="home" class="gr">

        <!-- ============ HERO ============ -->
        <section class="gr-hero">
          <v-container>
            <v-row align="center" justify="center" class="gr-hero-layout">
              <v-col cols="12" md="7" lg="7" class="text-center text-md-left">
                <h1 class="gr-h1 gr-hero-title">Especialistas en trámites de vehículos y transporte</h1>

                <p class="gr-lead gr-hero-lead">
                  Cuatro generaciones. {{ yearsOfExperience }} años. Un solo objetivo:
                  que no tengas que preocuparte por el papeleo.
                </p>

                <v-btn
                  :href="wa('Hola, necesito información sobre...')"
                  target="_blank"
                  rel="noopener"
                  x-large
                  class="gr-cta gr-hero-action mt-6"
                  depressed
                >
                  <v-icon left>mdi-whatsapp</v-icon>
                  Consulta sin compromiso
                </v-btn>
              </v-col>

              <v-col cols="12" md="5" lg="4" class="mt-10 mt-md-0">
                <aside class="gr-hero-card" aria-label="Servicios especializados">
                  <p class="gr-hero-card-label">Servicios especializados</p>
                  <div class="gr-hero-services">
                    <div class="gr-hero-service">
                      <v-icon>mdi-car-outline</v-icon>
                      <span>Vehículos y DGT</span>
                    </div>
                    <div class="gr-hero-service">
                      <v-icon>mdi-truck-outline</v-icon>
                      <span>Transporte profesional</span>
                    </div>
                    <div class="gr-hero-service">
                      <v-icon>mdi-shield-check-outline</v-icon>
                      <span>Seguros</span>
                    </div>
                  </div>
                </aside>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ INTRO ============ -->
        <section class="gr-section gr-section--intro">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8">
                <h2 class="gr-h2">Lo que otros complican, nosotros lo resolvemos</h2>

                <p class="gr-body">
                  En 1935 abrimos las puertas de esta gestoría con un objetivo claro:
                  ayudar a las personas con sus trámites.
                  Y eso es lo que hemos hecho desde entonces.
                </p>

                <p class="gr-body">
                  Cuatro generaciones después, seguimos aquí. En Tarragona. Con más experiencia y con una forma de trabajar que se adapta
                  a los tiempos: si quieres venir a vernos, perfecto. Si prefieres enviarnos
                  los documentos por WhatsApp desde casa, también.
                </p>

                <p class="gr-body gr-body--strong">
                  No somos una gestoría que hace de todo para todos. Somos especialistas
                  en vehículos, DGT y transporte profesional. Y esa especialización se nota.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ SERVICIOS (ACORDEONES) ============ -->
        <section class="gr-section gr-section--alt" id="servicios">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="9">
                <h2 class="gr-h2 text-center mb-8">¿Qué gestionamos?</h2>

                <v-expansion-panels flat multiple class="gr-panels">

                  <!-- Vehículos y DGT -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-car-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Trámites de vehículos y DGT</span>
                          <span class="gr-panel-sub">Los más habituales y los más complejos</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <ul class="gr-list">
                        <li v-for="(s, i) in vehiculos" :key="'v' + i">
                          <strong>{{ s.t }}</strong><template v-if="s.d">: {{ s.d }}</template>
                        </li>
                      </ul>

                      <p class="gr-list-title">Y los que otros no quieren tocar:</p>
                      <ul class="gr-list gr-list--tight">
                        <li v-for="(s, i) in vehiculosDificiles" :key="'vd' + i">{{ s }}</li>
                      </ul>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- Transporte -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-truck-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Trámites para profesionales del transporte</span>
                          <span class="gr-panel-sub">Empresas de transporte y autónomos del sector</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <ul class="gr-list">
                        <li v-for="(s, i) in transporte" :key="'t' + i">
                          <strong>{{ s.t }}</strong><template v-if="s.d">: {{ s.d }}</template>
                        </li>
                      </ul>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                  <!-- Seguros -->
                  <v-expansion-panel>
                    <v-expansion-panel-header>
                      <div class="gr-panel-head">
                        <v-icon class="gr-panel-icon">mdi-shield-check-outline</v-icon>
                        <span>
                          <span class="gr-panel-title">Seguros</span>
                          <span class="gr-panel-sub">El mejor seguro, mientras gestionamos tu trámite</span>
                        </span>
                      </div>
                    </v-expansion-panel-header>

                    <v-expansion-panel-content>
                      <p class="gr-body">
                        Mientras gestionamos tu trámite, podemos buscarte el mejor seguro.
                        Sin que tengas que llamar a ningún otro sitio. Trabajamos con las
                        principales compañías del mercado y ofrecemos:
                      </p>
                      <ul class="gr-list gr-list--tight">
                        <li v-for="(s, i) in seguros" :key="'s' + i">{{ s }}</li>
                      </ul>

                      <v-btn
                        :href="wa('Hola, quiero información sobre un seguro de...', whatsappSeguros)"
                        target="_blank"
                        rel="noopener"
                        depressed
                        class="gr-wa-btn mt-2"
                      >
                        <v-icon left>mdi-whatsapp</v-icon>
                        Pídenos presupuesto por WhatsApp
                      </v-btn>
                    </v-expansion-panel-content>
                  </v-expansion-panel>

                </v-expansion-panels>
              </v-col>
            </v-row>

            <!-- Formulario de presupuesto de seguros -->
            <v-row justify="center" class="mt-10">
              <v-col cols="12" md="9" lg="7">
                <formulario-seguro
                  lang="es"
                  :whatsapp="whatsappSeguros"
                  origen="home"
                  titulo-tag="h3"
                  sin-margen
                />
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ EXPERIENCIA ============ -->
        <section class="gr-anniversary">
          <v-container>
            <v-row align="center" justify="center">
              <v-col cols="12" md="4" class="text-center">
                <p class="gr-big-number">{{ yearsOfExperience }}</p>
                <p class="gr-big-label">años</p>
              </v-col>
              <v-col cols="12" md="7">
                <p class="gr-anniversary-text">
                  Cuatro generaciones resolviendo lo que otros complican.
                  Las mismas personas, la misma calle, la misma manera de trabajar:
                  tú nos cuentas qué necesitas y nosotros nos ocupamos del resto.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ RESEÑAS ============ -->
        <section class="gr-section gr-section--alt">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="8" class="text-center">
                <h2 class="gr-h2">Lo que dicen quienes ya han pasado por aquí</h2>
                <p class="gr-stars">
                  <v-icon v-for="n in 5" :key="n" class="gr-star">mdi-star</v-icon>
                  <span class="gr-stars-text">4,9 en Google</span>
                </p>
                <p class="gr-body-sm mb-6">No es un número que hayamos elegido nosotros.</p>

                <v-carousel
                  cycle
                  :interval="7000"
                  :height="$vuetify.breakpoint.smAndDown ? 560 : 400"
                  hide-delimiter-background
                  :show-arrows="$vuetify.breakpoint.mdAndUp"
                  class="gr-carousel"
                >
                  <v-carousel-item v-for="(r, i) in resenas" :key="'r' + i">
                    <blockquote class="gr-quote">
                      <p class="gr-quote-text">“{{ r.texto }}”</p>
                      <p class="gr-quote-author">{{ r.autor }}</p>
                    </blockquote>
                  </v-carousel-item>
                </v-carousel>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ CÓMO TRABAJAMOS ============ -->
        <section class="gr-section">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="10" lg="9">
                <h2 class="gr-h2 text-center">¿Cómo trabajamos?</h2>
                <p class="gr-body-sm text-center mb-8">
                  Sin colas. Sin desplazamientos innecesarios. Sin que tengas que volver tres veces.
                </p>

                <ol class="gr-steps">
                  <li v-for="(p, i) in pasos" :key="'p' + i" class="gr-step">
                    <span class="gr-step-num">{{ i + 1 }}</span>
                    <span class="gr-step-body">
                      <span class="gr-step-title">{{ p.t }}</span>
                      <span class="gr-step-text">{{ p.d }}</span>
                    </span>
                  </li>
                </ol>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ DÓNDE ESTAMOS ============ -->
        <section id="on-estem" class="gr-section gr-location">
          <v-container>
            <v-row align="center" justify="center">
              <v-col cols="12" md="5" lg="4">
                <p class="gr-eyebrow">Dónde estamos</p>
                <h2 class="gr-h2">En el centro de Tarragona</h2>
                <p class="gr-body">
                  Estamos en Rambla Nova, junto a la Font del Centenari.
                  Ven a vernos o envíanos la documentación y te ayudamos como te vaya mejor.
                </p>
                <a
                  class="gr-location-link"
                  href="https://goo.gl/maps/oB7HnhuBQtyNxuJt9"
                  target="_blank"
                  rel="noopener"
                >
                  <v-icon>mdi-directions</v-icon>
                  Cómo llegar
                </a>
              </v-col>

              <v-col cols="12" md="6" lg="5" class="mt-8 mt-md-0">
                <div class="gr-map-card">
                  <iframe
                    title="Mapa de Gestoría Riola en Tarragona"
                    src="https://www.google.com/maps?q=Rambla+Nova+110,+2%C2%BA+2%C2%AA,+43001+Tarragona&z=16&output=embed"
                    loading="lazy"
                    allowfullscreen
                    referrerpolicy="no-referrer-when-downgrade"
                  />
                  <div class="gr-map-address">
                    <v-icon>mdi-map-marker</v-icon>
                    <span>Rambla Nova, 110 · 2º 2ª · 43001 Tarragona</span>
                  </div>
                </div>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ FAQ ============ -->
        <section class="gr-section gr-section--faq">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8">
                <h2 class="gr-h2 text-center mb-8">Preguntas frecuentes</h2>

                <v-expansion-panels flat class="gr-panels gr-panels--faq">
                  <v-expansion-panel v-for="(f, i) in faq" :key="'f' + i">
                    <v-expansion-panel-header>
                      <span class="gr-faq-q">{{ f.q }}</span>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content>
                      <p class="gr-body">{{ f.a }}</p>
                    </v-expansion-panel-content>
                  </v-expansion-panel>
                </v-expansion-panels>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <!-- ============ CIERRE ============ -->
        <section class="gr-closing">
          <v-container>
            <v-row justify="center">
              <v-col cols="12" md="9" lg="8" class="text-center">
                <h2 class="gr-h2 gr-h2--light">{{ yearsOfExperience }} años resolviendo lo que otros complican</h2>

                <p class="gr-closing-text">
                  Si tienes un trámite de vehículo, necesitas gestiones con la DGT
                  o eres profesional del transporte, estamos aquí.
                </p>
                <p class="gr-closing-text">
                  Sin colas. Sin complicaciones. Con el respaldo de cuatro generaciones.
                </p>

                <v-btn
                  :href="wa('Hola, necesito información sobre...')"
                  target="_blank"
                  rel="noopener"
                  x-large
                  depressed
                  class="gr-wa-btn gr-wa-btn--xl mt-6"
                >
                  <v-icon left>mdi-whatsapp</v-icon>
                  Escríbenos por WhatsApp
                </v-btn>

                <p class="gr-closing-sign">
                  Gestoría Riola · Tarragona · Desde 1935 haciendo fácil lo difícil.
                </p>
              </v-col>
            </v-row>
          </v-container>
        </section>

        <Footer/>
      </div>
    </v-main>

    <menu-bottom/>

    <!-- ============ FAB WHATSAPP ============ -->
    <a
      :href="wa('Hola, necesito información sobre...')"
      target="_blank"
      rel="noopener"
      class="gr-fab"
      aria-label="Escríbenos por WhatsApp"
    >
      <v-icon class="gr-fab-icon">mdi-whatsapp</v-icon>
      <span class="gr-fab-text">Escríbenos</span>
    </a>

    <!-- ============ BOTÓN SUBIR ARRIBA ============ -->
    <button
      v-show="showTop"
      type="button"
      class="gr-totop"
      aria-label="Volver arriba"
      @click="scrollToTop"
    >
      <v-icon class="gr-totop-icon">mdi-chevron-up</v-icon>
    </button>
  </div>
</template>

<script>
  import MenuTop from "@/components/MenuTopEsp";
  import MenuBottom from "@/components/MenuBottomEsp";
  import Footer from "@/components/FooterEsp";
  import { mapGetters } from 'vuex';
  import FormularioSeguro from '@/components/FormularioSeguro';

  export default {
    name: 'PageHome',
    data: () => ({
      whatsapp: '34639086934',
      whatsappSeguros: '34624217274',

      showTop: false,

      vehiculos: [
        {
          t: 'Cambio de nombre de vehículos',
          d: 'el trámite más solicitado. Hay muchos tipos y muchos factores que afectan al precio y la documentación. Mándanos la info y te decimos exactamente qué necesitas y cuánto cuesta, sin compromiso.'
        },
        {
          t: 'Notificación de venta',
          d: 'si vendes un coche, esto te protege. Trámite rápido, económico y con precio fijo.'
        },
        {
          t: 'Matriculación de vehículos nuevos e importados',
          d: 'con toda la documentación en orden, en menos de 24 horas tienes número de matrícula.'
        },
        {
          t: 'Informes de vehículos',
          d: 'sabemos si tu vehículo tiene cargas, embargos, reservas de dominio o impuestos pendientes. Solo con la matrícula.'
        },
        { t: 'Duplicados de permisos de circulación y carnets de conducir', d: '' },
        { t: 'Bajas temporales y definitivas', d: '' },
        { t: 'Gestión de impuestos vinculados al vehículo', d: 'IVTM, ITP, IEDMT, CO2.' },
        { t: 'Inscripción y cancelación en el Registro de Bienes Muebles', d: '' }
      ],

      vehiculosDificiles: [
        'Transferencias por herencia',
        'Matriculaciones de vehículos agrícolas con cambios en el ROMA',
        'Vehículos que requieren certificados de transporte'
      ],

      transporte: [
        {
          t: 'Tarjetas de tacógrafo',
          d: 'renovaciones, nuevas tarjetas, duplicados y canjes. En unos 15 días desde que lo solicitamos.'
        },
        {
          t: 'Autorizaciones y tarjetas de transporte',
          d: 'MPC, MDL, MDP, OT, VT, VTC, VD. Cualquier tipo.'
        },
        { t: 'Certificados de conductor extracomunitario', d: '' },
        {
          t: 'Licencias comunitarias para transporte internacional',
          d: 'UE, Islandia, Noruega, Liechtenstein y Suiza. También autorizaciones bilaterales con Marruecos y Andorra.'
        },
        {
          t: 'Competencia profesional de transportista',
          d: 'título de gestor, convalidaciones y cambio de gestor.'
        },
        {
          t: 'Permisos especiales de transporte',
          d: 'vehículos que superan el peso o las dimensiones máximas.'
        },
        { t: 'Gestión del censo del gasóleo profesional', d: '' },
        { t: 'Asesoramiento ante inspecciones y ayudas para transportistas', d: '' }
      ],

      seguros: [
        'Vehículos: autos, motos, taxis, furgonetas, camiones y flotas',
        'Hogar y comunidades',
        'Comercios, profesionales y PYMES',
        'Transporte de mercancías',
        'Salud, vida y accidentes',
        'Viajes y responsabilidad civil'
      ],

      resenas: [
        {
          texto: 'Necesitaba hacer un cambio de titularidad de un coche de segunda mano que acababa de comprar, y no pudo ser más sencillo y económico. A través de WhatsApp envié toda la documentación necesaria y al día siguiente ya tenía el permiso de circulación. ¡Muchísimas gracias, Gestoría Riola!',
          autor: 'Lukas'
        },
        {
          texto: 'Esta es mi segunda vez con ellos. Son súper eficientes. El precio buenísimo. No tuve que esperar cola. En apenas 7 minutos estaba hecho el cambio de nombre. Son de las pocas empresas que he visto con 4,9 estrellas en Google… bien merecidas.',
          autor: 'Pedro'
        },
        {
          texto: 'Muy agradecido a Alex y a todo su equipo. Me ayudaron a gestionar la compra y la exportación de mi coche a Lituania de forma rápida y profesional. Comunicación clara, trato amable y total seriedad. Sin duda los recomiendo.',
          autor: 'Paulius'
        },
        {
          texto: 'Sin lugar a dudas la mejor gestoría de Tarragona. Seriedad, profesionalismo, pero por sobre todo buena gente Alex y todo su equipo. Te orientan en todas tus dudas con paciencia y amabilidad (cualidades difíciles de encontrar hoy en día). Desde que llegué a España todas mis compras y ventas de coches han pasado por esta gestoría y no tengo más que palabras de agradecimiento con el trato recibido. Cuando un servicio es de calidad merece ser reconocido, y no me canso de recomendarlos cada vez que alguien me pregunta por una gestoría de confianza.',
          autor: 'Micaela'
        }
      ],

      pasos: [
        { t: 'Cuéntanos qué necesitas', d: 'Por WhatsApp, por teléfono o en persona. Como prefieras.' },
        { t: 'Te decimos exactamente qué documentos hacen falta', d: 'Y el coste total, sin sorpresas.' },
        { t: 'Nos mandas la documentación', d: 'Por WhatsApp, por email o en persona. Como más cómodo te venga.' },
        { t: 'Nosotros gestionamos todo', d: 'Tú no tienes que hacer nada más.' },
        { t: 'Te avisamos cuando está listo', d: 'Normalmente en menos de 24 horas. A veces el mismo día. Y si necesitas que te lo enviemos a casa, también lo hacemos.' }
      ],

      faq: [
        {
          q: '¿Cuánto cuesta el cambio de nombre?',
          a: 'Depende del vehículo y del tipo de operación. Mándanos la documentación y te decimos el precio exacto sin compromiso.'
        },
        {
          q: '¿Cuánto tarda?',
          a: 'Si no hay ningún impedimento con el vehículo, en menos de 24 horas tienes el permiso de circulación.'
        },
        {
          q: '¿El vehículo tiene cargas o embargos?',
          a: 'Con solo la matrícula te lo decimos al momento, sin coste.'
        },
        {
          q: '¿Puedo hacer todo sin venir a la oficina?',
          a: 'Sí. Muchos clientes lo gestionan todo por WhatsApp desde el principio hasta el final.'
        },
        {
          q: '¿Hacéis seguros?',
          a: 'Sí. Mientras gestionamos tu trámite, podemos buscarte el mejor seguro del mercado.'
        },
        {
          q: '¿Enviáis la documentación a domicilio?',
          a: 'Sí. Todos los envíos son certificados y salen el mismo día que recibimos la documentación. Al día siguiente está en tu casa.'
        }
      ]
    }),

    computed: {
      ...mapGetters(['collapseSidebar']),
      yearsOfExperience() {
        return new Date().getFullYear() - 1935;
      },
    },

    mounted() {
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    },

    beforeDestroy() {
      window.removeEventListener('scroll', this.onScroll);
    },

    methods: {
      onScroll() {
        this.showTop = window.pageYOffset > 400;
      },

      scrollToTop() {
        window.scrollTo({ top: 0, behavior: 'smooth' });
      },

      wa(mensaje, numero) {
        const destino = numero || this.whatsapp;
        return 'https://wa.me/' + destino + '?text=' + encodeURIComponent(mensaje);
      }
    },

    components: {
      FormularioSeguro,
      Footer,
      MenuTop,
      MenuBottom
    }
  }
</script>

<style scoped>
  .gr {
    --gr-slate: #454E4F;
    --gr-brand: #900A30;
    --gr-brand-dark: #6E0724;
    --gr-gold: #F2A900;
    --gr-ink: #252A2B;
    --gr-mute: rgba(37, 42, 43, 0.72);
    --gr-alt: #F4F7F9;
  }

  /* ---------- Tipografía base ---------- */
  .gr-eyebrow {
    text-transform: uppercase;
    letter-spacing: 0.16em;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--gr-brand);
    margin-bottom: 12px;
  }

  .gr-h1 {
    font-size: 2.6rem;
    line-height: 1.12;
    font-weight: 700;
    color: var(--gr-slate);
    letter-spacing: -0.02em;
  }

  .gr-h2 {
    font-size: 1.85rem;
    line-height: 1.2;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 20px;
    letter-spacing: -0.01em;
  }

  .gr-h2--light { color: #fff; }

  .gr-h3 {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 4px;
  }

  .gr-lead {
    font-size: 1.2rem;
    line-height: 1.55;
    color: var(--gr-mute);
    margin-top: 18px;
  }

  .gr-body {
    font-size: 1.05rem;
    line-height: 1.7;
    color: var(--gr-mute);
    margin-bottom: 18px;
  }

  .gr-body--strong {
    color: var(--gr-ink);
    font-weight: 600;
  }

  .gr-body-sm {
    font-size: 0.98rem;
    line-height: 1.6;
    color: var(--gr-mute);
  }

  .gr-caption {
    font-size: 0.85rem;
    color: var(--gr-mute);
  }

  /* ---------- Secciones ---------- */
  #home .gr-hero {
    position: relative;
    isolation: isolate;
    overflow: hidden;
    padding: 72px 0 56px;
    background-image:
      linear-gradient(135deg, rgba(37, 42, 43, 0.86) 0%, rgba(37, 42, 43, 0.62) 52%, rgba(144, 10, 48, 0.52) 100%),
      url("~@/assets/img/camiones.jpeg");
    background-size: cover, cover;
    background-position: center, center;
    background-repeat: no-repeat, no-repeat;
  }

  /* Texto del hero en claro sobre la foto */
  #home .gr-hero .gr-hero-eyebrow { color: var(--gr-gold); }
  #home .gr-hero .gr-hero-title { color: #fff; }
  #home .gr-hero .gr-hero-lead { color: rgba(255, 255, 255, 0.92); }
  #home .gr-hero .gr-hero-support { color: rgba(255, 255, 255, 0.9); }
  #home .gr-hero .gr-hero-support .v-icon { color: var(--gr-gold); }

  /* Móvil: la foto llena todo el hero sin bandas ni cortes */
  @media (max-width: 600px) {
    #home .gr-hero {
      background-color: var(--gr-ink);
      background-size: cover, cover;
      background-position: center, center 65%;
    }
  }

  .gr-hero::before,
  .gr-hero::after {
    content: '';
    position: absolute;
    z-index: -1;
    border: 1px solid rgba(144, 10, 48, 0.10);
    border-radius: 50%;
    pointer-events: none;
  }

  .gr-hero::before {
    width: 460px;
    height: 460px;
    top: -315px;
    right: 8%;
  }

  .gr-hero::after {
    width: 260px;
    height: 260px;
    bottom: -190px;
    left: 10%;
  }

  .gr-hero .v-container {
    position: relative;
    z-index: 1;
  }

  .gr-hero-eyebrow,
  .gr-hero-title,
  .gr-hero-lead,
  .gr-hero-action,
  .gr-hero-card {
    animation: gr-hero-reveal 720ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-hero-eyebrow { animation-delay: 110ms; }
  .gr-hero-title { animation-delay: 190ms; }
  .gr-hero-lead { animation-delay: 290ms; }
  .gr-hero-action { animation-delay: 390ms; }
  .gr-hero-card { animation-delay: 250ms; }

  .gr-hero-title {
    font-size: 3.15rem;
    line-height: 1.08;
    max-width: 700px;
  }

  .gr-hero-lead {
    max-width: 610px;
  }

  .gr-hero-support {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    margin: 22px 0 0;
    color: var(--gr-slate);
    font-size: 0.92rem;
    font-weight: 500;
  }

  .gr-hero-support .v-icon {
    color: var(--gr-brand);
  }

  .gr-hero-card {
    position: relative;
    overflow: hidden;
    padding: 32px 30px 28px;
    background: rgba(255, 255, 255, 0.92);
    border: 1px solid rgba(69, 78, 79, 0.13);
    border-radius: 20px;
    box-shadow: 0 22px 52px rgba(37, 42, 43, 0.12);
  }

  .gr-hero-card-label {
    margin: 0 0 14px;
    color: var(--gr-brand);
    font-size: 0.76rem;
    font-weight: 700;
    letter-spacing: 0.13em;
    text-align: center;
    text-transform: uppercase;
  }

  .gr-hero-services {
    border-top: 1px solid rgba(69, 78, 79, 0.13);
  }

  .gr-hero-service {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 0;
    border-bottom: 1px solid rgba(69, 78, 79, 0.13);
    color: var(--gr-slate);
    font-size: 0.98rem;
    font-weight: 600;
  }

  .gr-hero-service .v-icon {
    color: var(--gr-brand);
    font-size: 22px;
  }

  .gr-hero-action.v-btn {
    box-shadow: 0 10px 24px rgba(144, 10, 48, 0.22);
    transition: transform 180ms ease, box-shadow 180ms ease;
  }

  .gr-hero-action.v-btn:hover {
    transform: translateY(-3px);
    box-shadow: 0 14px 30px rgba(144, 10, 48, 0.28);
  }

  @keyframes gr-hero-reveal {
    from { opacity: 0; transform: translateY(18px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .gr-section { padding: 64px 0; }

  .gr-section--alt { background-color: var(--gr-alt); }

  #home .gr-section--intro,
  #home .gr-section--faq {
    position: relative;
  }

  #home .gr-section--intro {
    background: linear-gradient(135deg, #efe6e9 0%, #f7f1f3 70%);
    box-shadow: inset 0 14px 26px -18px rgba(37, 42, 43, 0.22);
  }

  #home .gr-section--faq { background: #fff; }

  .gr-section--intro::before,
  .gr-section--faq::before {
    content: '';
    position: absolute;
    top: -42px;
    left: 0;
    width: 100%;
    height: 43px;
    pointer-events: none;
    clip-path: polygon(0 100%, 100% 0, 100% 100%);
  }

  .gr-section--intro::before { background: #efe6e9; }
  .gr-section--faq::before { background: #fff; }

  /* Acento de marca que marca el inicio de la sección */
  #home .gr-section--intro .gr-h2 { position: relative; padding-top: 22px; }

  #home .gr-section--intro .gr-h2::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 54px;
    height: 4px;
    border-radius: 999px;
    background: var(--gr-brand);
  }

  /* ---------- CTA ---------- */
  .gr-cta.v-btn {
    background-color: var(--gr-brand) !important;
    color: #fff !important;
    font-weight: 700;
    letter-spacing: 0.01em;
    text-transform: none;
    border-radius: 999px;
    padding: 0 30px !important;
  }

  .gr-cta--form.v-btn {
    border-radius: 8px;
    margin-top: 8px;
  }

  /* ---------- Acordeones ---------- */
  .gr-panels >>> .v-expansion-panel {
    background-color: #fff;
    border: 1px solid rgba(69, 78, 79, 0.16);
    border-radius: 10px !important;
    margin-bottom: 12px;
  }

  .gr-panels >>> .v-expansion-panel::before { box-shadow: none; }

  .gr-panel-head {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .gr-panel-icon.v-icon {
    color: var(--gr-brand);
    font-size: 30px;
  }

  .gr-panel-title {
    display: block;
    font-size: 1.12rem;
    font-weight: 700;
    color: var(--gr-slate);
    line-height: 1.3;
  }

  .gr-panel-sub {
    display: block;
    font-size: 0.9rem;
    color: var(--gr-mute);
    margin-top: 2px;
  }

  .gr-list {
    padding-left: 20px;
    margin: 0 0 8px;
  }

  .gr-list li {
    font-size: 1rem;
    line-height: 1.65;
    color: var(--gr-mute);
    margin-bottom: 10px;
  }

  .gr-list li strong { color: var(--gr-ink); }

  .gr-list--tight li { margin-bottom: 6px; }

  .gr-list-title {
    font-weight: 700;
    color: var(--gr-slate);
    margin: 18px 0 8px;
  }

  .gr-faq-q {
    font-size: 1.05rem;
    font-weight: 600;
    color: var(--gr-slate);
  }

  /* ---------- Formulario ---------- */
  .gr-form-card {
    background: #fff;
    border: 1px solid rgba(69, 78, 79, 0.16);
    border-radius: 14px;
    padding: 32px;
  }

  .gr-wa-btn--xl.v-btn {
    height: 52px;
    font-size: 1.05rem;
    padding: 0 32px !important;
  }

  .gr-wa-btn.v-btn {
    background-color: #25D366 !important;
    color: #fff !important;
    font-weight: 700;
    text-transform: none;
    border-radius: 999px;
    height: auto !important;
    min-height: 48px;
    max-width: 100%;
    white-space: normal;
    padding: 10px 22px !important;
  }

  /* Permite que el texto largo del botón se parta en móvil sin desbordar */
  .gr-wa-btn.v-btn .v-btn__content {
    white-space: normal;
    max-width: 100%;
    flex: 1 1 auto;
    line-height: 1.25;
  }

  .gr-wa-alt {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 22px;
    padding-top: 18px;
    border-top: 1px solid rgba(69, 78, 79, 0.16);
  }

  .gr-wa-alt-text {
    font-size: 0.95rem;
    color: var(--gr-mute);
  }

  /* ---------- Experiencia ---------- */
  #home .gr-anniversary {
    min-height: 360px;
    position: relative;
    isolation: isolate;
    overflow: hidden;
    background:
      radial-gradient(circle at 13% 50%, rgba(255, 255, 255, 0.10) 0 1px, transparent 1.5px),
      radial-gradient(circle at 13% 50%, rgba(255, 255, 255, 0.04) 0 110px, transparent 260px),
      linear-gradient(112deg, #6E0724 0%, #790827 52%, #900A30 100%);
    color: #fff;
    padding: 42px 0;
  }

  .gr-anniversary::before,
  .gr-anniversary::after {
    content: '';
    position: absolute;
    z-index: -1;
    border: 1px solid rgba(255, 255, 255, 0.11);
    border-radius: 50%;
    pointer-events: none;
  }

  .gr-anniversary::before {
    width: 430px;
    height: 430px;
    top: -310px;
    right: 7%;
  }

  .gr-anniversary::after {
    width: 270px;
    height: 270px;
    bottom: -190px;
    left: 32%;
  }

  .gr-anniversary .v-container {
    position: relative;
    z-index: 1;
  }

  .gr-big-number {
    font-size: 8rem;
    line-height: 0.9;
    font-weight: 800;
    color: #fff;
    letter-spacing: -0.05em;
    margin: 0;
    animation: gr-anniversary-reveal 700ms cubic-bezier(0.22, 1, 0.36, 1) both;
    transition: transform 220ms ease;
  }

  .gr-big-label {
    text-transform: uppercase;
    letter-spacing: 0.3em;
    font-size: 0.9rem;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.75);
    margin: 8px 0 0;
    animation: gr-anniversary-reveal 650ms 100ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-anniversary-text {
    font-size: 1.25rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.9);
    margin: 0;
    animation: gr-anniversary-reveal 700ms 180ms cubic-bezier(0.22, 1, 0.36, 1) both;
  }

  .gr-anniversary:hover .gr-big-number {
    transform: translateY(-4px);
  }

  @keyframes gr-anniversary-reveal {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  /* ---------- Equipo ---------- */
  .gr-team-photo {
    border-radius: 14px;
    overflow: hidden;
  }

  /* ---------- Reseñas ---------- */
  .gr-stars { margin-bottom: 4px; }

  .gr-star.v-icon {
    color: var(--gr-gold);
    font-size: 22px;
  }

  .gr-stars-text {
    font-weight: 700;
    color: var(--gr-slate);
    margin-left: 10px;
    vertical-align: middle;
  }

  .gr-carousel >>> .v-window__container { align-items: center; }

  .gr-quote {
    max-width: 720px;
    margin: 0 auto;
    padding: 0 24px;
    border: 0;
  }

  .gr-quote-text {
    font-size: 1.1rem;
    line-height: 1.58;
    color: var(--gr-ink);
    font-style: italic;
    margin-bottom: 16px;
  }

  .gr-quote-author {
    background: none;
    padding: 0;
    margin: 0;
    font-size: 0.95rem;
    font-weight: 700;
    color: var(--gr-brand);
  }

  /* ---------- Pasos ---------- */
  .gr-steps {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  .gr-step {
    display: flex;
    align-items: flex-start;
    gap: 20px;
    padding: 18px 0;
    border-bottom: 1px solid rgba(69, 78, 79, 0.16);
  }

  .gr-step:last-child { border-bottom: 0; }

  .gr-step-num {
    flex: 0 0 40px;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    background: var(--gr-brand);
    color: #fff;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .gr-step-title {
    display: block;
    font-size: 1.08rem;
    font-weight: 700;
    color: var(--gr-slate);
    margin-bottom: 2px;
  }

  .gr-step-text {
    display: block;
    color: var(--gr-mute);
    line-height: 1.6;
  }

  /* ---------- Ubicación ---------- */
  #home .gr-location {
    min-height: auto;
    padding: 76px 0;
    background: var(--gr-alt);
    scroll-margin-top: 88px;
  }

  .gr-location-link {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 8px;
    color: var(--gr-brand);
    font-weight: 700;
    text-decoration: none;
  }

  .gr-location-link .v-icon { color: var(--gr-brand); }

  .gr-location-link:hover { color: var(--gr-brand-dark); }

  .gr-map-card {
    position: relative;
    overflow: hidden;
    border: 1px solid rgba(69, 78, 79, 0.15);
    border-radius: 18px;
    box-shadow: 0 18px 42px rgba(37, 42, 43, 0.13);
  }

  .gr-map-card iframe {
    display: block;
    width: 100%;
    height: 360px;
    border: 0;
  }

  .gr-map-address {
    position: absolute;
    right: 16px;
    bottom: 16px;
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 10px 14px;
    background: rgba(255, 255, 255, 0.94);
    border-radius: 8px;
    box-shadow: 0 5px 18px rgba(37, 42, 43, 0.15);
    color: var(--gr-slate);
    font-size: 0.86rem;
    font-weight: 600;
  }

  .gr-map-address .v-icon { color: var(--gr-brand); }

  /* ---------- Cierre ---------- */
  .gr-closing {
    position: relative;
    padding: 72px 0 88px;
    background-color: var(--gr-brand);
    background-image:
      linear-gradient(180deg, rgba(37, 42, 43, 0.55) 0%, rgba(110, 7, 36, 0.35) 100%),
      url("~@/assets/img/fondo.jpeg");
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    background-attachment: fixed; /* efecto parallax: la imagen se queda fija al hacer scroll */
  }

  /* En móviles el fondo fijo no funciona bien (iOS lo ignora), pasa a scroll */
  @media (max-width: 900px) {
    .gr-closing { background-attachment: scroll; }
  }

  .gr-closing-text {
    font-size: 1.1rem;
    line-height: 1.65;
    color: rgba(255, 255, 255, 0.88);
    margin-bottom: 10px;
  }

  .gr-closing-sign {
    margin-top: 32px;
    font-size: 0.9rem;
    color: rgba(255, 255, 255, 0.6);
  }

  /* ---------- FAB WhatsApp ---------- */
  .gr-fab {
    position: fixed;
    right: 20px;
    bottom: 76px;
    z-index: 20;
    display: inline-flex;
    align-items: center;
    gap: 10px;
    background: #25D366;
    color: #fff !important;
    text-decoration: none;
    font-weight: 700;
    padding: 14px 22px;
    border-radius: 999px;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    transition: transform 0.15s ease;
  }

  .gr-fab:hover { transform: translateY(-2px); }

  .gr-fab-icon.v-icon { color: #fff; font-size: 26px; }

  .gr-fab:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  /* ---------- Botón subir arriba ---------- */
  .gr-totop {
    position: fixed;
    right: 20px;
    bottom: 144px; /* justo encima del FAB de WhatsApp */
    z-index: 20;
    width: 48px;
    height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #900A30;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.22);
    transition: transform 0.15s ease, background 0.15s ease;
  }

  .gr-totop:hover { transform: translateY(-2px); background: #6E0724; }

  .gr-totop-icon.v-icon { color: #fff; font-size: 28px; }

  .gr-totop:focus-visible {
    outline: 3px solid #fff;
    outline-offset: 3px;
  }

  /* ---------- Responsive ---------- */
  @media (max-width: 960px) {
    .gr-h1 { font-size: 2.1rem; }
    .gr-hero-title { font-size: 2.45rem; }
    .gr-h2 { font-size: 1.55rem; }
    .gr-big-number { font-size: 6rem; }
    .gr-anniversary-text { font-size: 1.1rem; text-align: center; }
    #home .gr-anniversary { min-height: 320px; }
    .gr-hero-support { justify-content: flex-start; }
    #home .gr-location { padding: 60px 0; }
  }

  @media (max-width: 600px) {
    #home .gr-hero { padding: 48px 0 40px; }
    .gr-section { padding: 48px 0; }
    .gr-h1 { font-size: 1.8rem; }
    .gr-hero-title { font-size: 2.15rem; }
    .gr-lead { font-size: 1.05rem; }
    .gr-hero-support { justify-content: center; }
    .gr-hero-card { padding: 28px 24px 24px; }
    .gr-map-card iframe { height: 300px; }
    .gr-map-address { right: 12px; bottom: 12px; }
    .gr-form-card { padding: 22px; }
    .gr-quote-text { font-size: 0.98rem; line-height: 1.5; }
    .gr-fab { padding: 0; width: 56px; height: 56px; justify-content: center; }
    .gr-fab-text { display: none; }

    /* Botones grandes: que se adapten al ancho y partan texto en móvil */
    .gr-cta.v-btn,
    .gr-hero-action.v-btn,
    .gr-wa-btn.v-btn,
    .gr-wa-btn--xl.v-btn {
      height: auto !important;
      min-height: 46px;
      max-width: 100%;
      white-space: normal;
      padding-top: 10px !important;
      padding-bottom: 10px !important;
      font-size: 0.98rem;
    }
    .gr-cta.v-btn .v-btn__content,
    .gr-hero-action.v-btn .v-btn__content,
    .gr-wa-btn--xl.v-btn .v-btn__content {
      white-space: normal;
      max-width: 100%;
      line-height: 1.25;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .gr-fab, .gr-big-number, .gr-hero-action.v-btn { transition: none; }
    .gr-big-number, .gr-big-label, .gr-anniversary-text,
    .gr-hero-eyebrow, .gr-hero-title, .gr-hero-lead, .gr-hero-action { animation: none; }
  }
</style>
